import { query } from '@warp-drive/utilities/json-api';
import type { LpnPageExtension as LpnPage } from '../data/resources/lpn-page.ext.ts';
import type { TasProgramTemplateInstitutionExtension as TasProgramTemplateInstitution } from '../data/resources/tas-program-template-institution.ext.ts';

/** The API's maximum page size. Rows beyond it are not loaded, so callers see at most this many. */
const MAX_PAGE_SIZE = 400;

/** Multi-select filters on the LPN catalog, keyed by the option keys in tio-common's lpn-options. */
export interface LpnPageFilters {
  search?: string;
  savings?: string[];
  format?: string[];
  programs?: string[];
  areasOfStudy?: string[];
}

/**
 * The whole LPN catalog as seen by one company, with each page's company override sideloaded so
 * callers can merge it. Company-aware filters carry the company setting id first, as the API expects.
 * Visibility (disabled pages, other companies' pages) is applied by the caller, not the API.
 *
 * @param companySettingId the viewer's company setting; scopes overrides and company-aware filters
 * @param filters search text and option keys from tio-common's lpn-options; empty ones are omitted
 * @returns a query for `lpn-page` records, sorted by school name, at most 400 of them
 */
export function lpnCatalogQuery(companySettingId: string, filters: LpnPageFilters = {}) {
  const scoped = (values: string[] | undefined) =>
    values?.length ? [companySettingId, ...values].join(',') : undefined;

  const params: Record<string, string | number> = {
    include: 'company-lpn-settings,company-lpn-settings.company-setting',
    // Only the company-setting id is needed to match overrides; this keeps the payload small.
    'fields[company-settings]': 'is-global',
    sort: 'school-name',
    'page[limit]': MAX_PAGE_SIZE,
  };
  const search = filters.search?.trim();
  if (search) params['filter[search]'] = search;
  const scopedFilters = {
    'filter[savings]': scoped(filters.savings),
    'filter[format]': scoped(filters.format),
    'filter[programs]': scoped(filters.programs),
    'filter[areas-of-study]': scoped(filters.areasOfStudy),
  };
  for (const [key, value] of Object.entries(scopedFilters)) {
    if (value) params[key] = value;
  }

  return query<LpnPage>('lpn-page', params);
}

/**
 * The curated school lists of the given TA program templates, with the institutions sideloaded so
 * their ids can be read.
 *
 * @param programTemplateIds templates whose curated lists to load; one request covers them all
 * @returns a query for `tas-program-template-institution` records, at most 400 across all templates
 */
export function programTemplateSchoolsQuery(programTemplateIds: string[]) {
  return query<TasProgramTemplateInstitution>('tas-program-template-institution', {
    'filter[tas-program-template]': programTemplateIds.join(','),
    include: 'accredited-institution,unaccredited-institution',
    'page[limit]': MAX_PAGE_SIZE,
  });
}
